import React, { useState } from 'react';
import { Copy, Check, AlertTriangle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Copy to Clipboard Button
export const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`copy-btn ${copied ? 'copied' : ''}`}
      title={`Copy ${label}`}
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

// Loading Button - prevents double clicks
export const LoadingButton = ({ 
  onClick, 
  loading, 
  children, 
  className = 'btn-primary',
  disabled = false,
  type = 'button',
  ...props 
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={loading || disabled}
      className={`${className} ${loading ? 'btn-loading' : ''}`}
      {...props}
    >
      {loading ? <span className="opacity-0">{children}</span> : children}
    </button>
  );
};

// Confirmation Dialog
export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning', // 'warning' | 'danger'
  loading = false
}) => {
  if (!isOpen) return null;

  const Icon = variant === 'danger' ? AlertCircle : AlertTriangle;
  const iconColor = variant === 'danger' ? 'text-red-500' : 'text-yellow-500';
  const confirmBtnClass = variant === 'danger' ? 'btn-danger' : 'btn-primary';

  return (
    <div className="confirm-dialog-overlay" onClick={onClose}>
      <div 
        className={`confirm-dialog confirm-dialog-${variant}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className={`p-2 rounded-full ${variant === 'danger' ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 text-sm">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button 
            onClick={onClose} 
            className="btn-secondary"
            disabled={loading}
          >
            {cancelText}
          </button>
          <LoadingButton
            onClick={onConfirm}
            loading={loading}
            className={confirmBtnClass}
            data-testid="confirm-dialog-confirm"
          >
            {confirmText}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

// Format amount with color coding
export const ColoredAmount = ({ amount, type, currency = '' }) => {
  const isPositive = type === 'deposit' || amount > 0;
  const colorClass = isPositive ? 'amount-positive' : 'amount-negative';
  const prefix = isPositive && type !== 'deposit' ? '+' : '';
  
  const formattedAmount = typeof amount === 'number' 
    ? amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : amount;

  return (
    <span className={colorClass}>
      {prefix}{currency}{formattedAmount}
    </span>
  );
};

// Large amount warning threshold (50 Lakhs = 5,000,000)
export const LARGE_AMOUNT_THRESHOLD_INR = 5000000;
export const LARGE_AMOUNT_THRESHOLD_USDT = 60000; // Approx equivalent

export const isLargeAmount = (amount, currency) => {
  if (currency === 'INR') {
    return amount >= LARGE_AMOUNT_THRESHOLD_INR;
  }
  return amount >= LARGE_AMOUNT_THRESHOLD_USDT;
};

// Format large amounts for display
export const formatLargeAmount = (amount, currency) => {
  if (currency === 'INR' && amount >= 100000) {
    const lakhs = amount / 100000;
    return `${lakhs.toFixed(2)}L`;
  }
  return amount.toLocaleString('en-IN');
};

// ==================== INPUT VALIDATION ====================

// Validate amount (positive number, within limits)
export const validateAmount = (amount, currency = 'INR') => {
  const errors = [];
  const numAmount = parseFloat(amount);
  
  if (amount === '' || amount === null || amount === undefined) {
    errors.push('Amount is required');
    return { valid: false, errors };
  }
  
  if (isNaN(numAmount)) {
    errors.push('Please enter a valid number');
    return { valid: false, errors };
  }
  
  if (numAmount <= 0) {
    errors.push('Amount must be greater than 0');
    return { valid: false, errors };
  }
  
  if (numAmount > 100000000) { // 10 Crore limit
    errors.push('Amount exceeds maximum limit (10 Cr)');
    return { valid: false, errors };
  }
  
  // Decimal places check
  const decimalPart = amount.toString().split('.')[1];
  if (decimalPart && decimalPart.length > 2) {
    errors.push('Maximum 2 decimal places allowed');
    return { valid: false, errors };
  }
  
  return { valid: true, errors: [], value: numAmount };
};

// Validate date
export const validateDate = (dateStr, options = {}) => {
  const { 
    allowFuture = false, 
    allowPast = true,
    minDate = null,
    maxDate = null 
  } = options;
  
  const errors = [];
  
  if (!dateStr) {
    errors.push('Date is required');
    return { valid: false, errors };
  }
  
  const date = new Date(dateStr);
  date.setHours(0, 0, 0, 0);  // Normalize to midnight
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (isNaN(date.getTime())) {
    errors.push('Please enter a valid date');
    return { valid: false, errors };
  }
  
  // Allow today's date - only block strictly future dates
  if (!allowFuture && date > today) {
    errors.push('Future dates are not allowed');
    return { valid: false, errors };
  }
  
  if (!allowPast && date < today) {
    errors.push('Past dates are not allowed');
    return { valid: false, errors };
  }
  
  if (minDate && date < new Date(minDate)) {
    errors.push(`Date cannot be before ${minDate}`);
    return { valid: false, errors };
  }
  
  if (maxDate && date > new Date(maxDate)) {
    errors.push(`Date cannot be after ${maxDate}`);
    return { valid: false, errors };
  }
  
  return { valid: true, errors: [] };
};

// Validate required fields
export const validateRequired = (value, fieldName = 'This field') => {
  if (value === '' || value === null || value === undefined) {
    return { valid: false, error: `${fieldName} is required` };
  }
  if (typeof value === 'string' && value.trim() === '') {
    return { valid: false, error: `${fieldName} cannot be empty` };
  }
  return { valid: true, error: null };
};

// Validate percentage
export const validatePercentage = (value, fieldName = 'Percentage') => {
  const numValue = parseFloat(value);
  
  if (value === '' || value === null || value === undefined) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  if (isNaN(numValue)) {
    return { valid: false, error: 'Please enter a valid number' };
  }
  
  if (numValue < 0 || numValue > 100) {
    return { valid: false, error: `${fieldName} must be between 0 and 100` };
  }
  
  return { valid: true, error: null, value: numValue };
};

// ==================== DUPLICATE DETECTION ====================

// Check for similar strings (for duplicate detection)
export const isSimilarString = (str1, str2, threshold = 0.8) => {
  if (!str1 || !str2) return false;
  
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  // Exact match
  if (s1 === s2) return true;
  
  // Contains check
  if (s1.includes(s2) || s2.includes(s1)) return true;
  
  // Levenshtein distance based similarity
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return true;
  
  const editDistance = levenshteinDistance(s1, s2);
  const similarity = (longer.length - editDistance) / longer.length;
  
  return similarity >= threshold;
};

// Levenshtein distance helper
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]) + 1;
      }
    }
  }
  
  return dp[m][n];
};

// Check for potential duplicate client
export const findDuplicateClient = (newClient, existingClients) => {
  const duplicates = existingClients.filter(client => {
    // Check client code
    if (client.client_code && newClient.client_code &&
        client.client_code.toLowerCase() === newClient.client_code.toLowerCase()) {
      return true;
    }
    // Check name similarity
    if (isSimilarString(client.name, newClient.name, 0.85)) {
      return true;
    }
    return false;
  });
  
  return duplicates;
};

// Check for potential duplicate ledger entry
export const findDuplicateLedgerEntry = (newEntry, existingEntries, timeWindowHours = 24) => {
  const newDate = new Date(newEntry.date);
  
  const duplicates = existingEntries.filter(entry => {
    // Same client
    if (entry.client_id !== newEntry.client_id) return false;
    
    // Same type
    if (entry.type !== newEntry.type) return false;
    
    // Same amount (within 1% tolerance)
    const amountDiff = Math.abs(entry.amount - newEntry.amount) / newEntry.amount;
    if (amountDiff > 0.01) return false;
    
    // Within time window
    const entryDate = new Date(entry.date);
    const hoursDiff = Math.abs(newDate - entryDate) / (1000 * 60 * 60);
    if (hoursDiff > timeWindowHours) return false;
    
    return true;
  });
  
  return duplicates;
};

// ==================== FORM VALIDATION HELPER ====================

// Inline validation message component
export const ValidationMessage = ({ error, show = true }) => {
  if (!error || !show) return null;
  
  return (
    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" />
      {error}
    </p>
  );
};

// Duplicate warning component
export const DuplicateWarning = ({ duplicates, type = 'entry', onProceed, onCancel }) => {
  if (!duplicates || duplicates.length === 0) return null;
  
  return (
    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-yellow-800 mb-2">
            Potential duplicate {type} detected!
          </p>
          <p className="text-sm text-yellow-700 mb-3">
            Similar {type}(s) already exist:
          </p>
          <ul className="text-sm text-yellow-700 mb-3 space-y-1">
            {duplicates.slice(0, 3).map((dup, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                {dup.display || dup.name || dup.client_code || `${dup.type}: ${dup.amount}`}
              </li>
            ))}
            {duplicates.length > 3 && (
              <li className="text-yellow-600">...and {duplicates.length - 3} more</li>
            )}
          </ul>
          <div className="flex gap-2">
            <button
              onClick={onProceed}
              className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
            >
              Proceed Anyway
            </button>
            <button
              onClick={onCancel}
              className="px-3 py-1.5 bg-white text-yellow-700 text-sm rounded border border-yellow-400 hover:bg-yellow-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
